// agent-fab-shell.jsx — the SHARED mechanics behind the two floating voice-agent
// surfaces: the consult-only CommandAgent (screens/consult/CommandAgent.jsx) and
// the everywhere GlobalAgentButton (components/app/GlobalAgentButton.jsx).
//
// Only the parts that were byte-identical in both live here: the localStorage
// position store, the pointer-drag gesture (4px move threshold · document-level
// pointermove/pointerup · snap-to-side on release, tap when it never moved), the
// FAB's geometry/transition/transform shell, the anchored-panel placement maths,
// and the pop/chip/recording-ring keyframes. Everything that genuinely DIFFERS
// stays at the call site and is passed in:
//   · the clamp/snap geometry (the consult FAB must clear the sign-and-send
//     commit strip; the global FAB must clear the WorldSwitcher / ward dock),
//   · the tap behaviour (interpret vs. submit),
//   · the FAB's colours, glyphs and the consult-only long-press stage machine,
//   · the panel's width, height reserve and contents.
// This is a pure de-duplication: no visual or behavioural change on either side.
import React from 'react';

/**
 * A namespaced position store. Each surface keeps its own localStorage key so the
 * two FABs never share a resting spot.
 */
export function fabPositionStore(storageKey) {
  return {
    loadPos: () => { try { return JSON.parse(localStorage.getItem(storageKey)) || null; } catch (_) { return null; } },
    savePos: (p) => { try { localStorage.setItem(storageKey, JSON.stringify(p)); } catch (_) {} },
  };
}

/**
 * The fluid drag gesture (AssistiveTouch idiom). Returns the `dragging` flag the
 * FAB style needs plus the `onPointerDown` handler.
 *
 *  pos        current { x, y }
 *  setPos     state setter for the position
 *  clampDrag  (p) => p        — keeps a live drag inside that surface's safe box
 *  snap       ({ base, event, startX, startY }) => p  — the spring-back target
 *  savePos    (p) => void     — persist the snapped position
 *  onTap      () => void      — a press that never moved is a tap
 */
export function useFabDrag({ pos, setPos, clampDrag, snap, savePos, onTap }) {
  const [dragging, setDragging] = React.useState(false);
  const dragRef = React.useRef({ moved: false });
  // The handlers read the LATEST props at pointer-down time, matching the old
  // per-render closure exactly while keeping the callback identity stable.
  const latest = React.useRef(null);
  latest.current = { pos, setPos, clampDrag, snap, savePos, onTap };

  const onPointerDown = React.useCallback((e) => {
    const cur = latest.current;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const base = { ...cur.pos };
    dragRef.current.moved = false;
    setDragging(true);
    const move = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragRef.current.moved = true;
      latest.current.setPos(latest.current.clampDrag({ x: base.x + dx, y: base.y + dy }));
    };
    const up = (ev) => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setDragging(false);
      if (dragRef.current.moved) {
        const snapped = latest.current.snap({ base, event: ev, startX, startY });
        latest.current.setPos(snapped); latest.current.savePos(snapped);
      } else {
        latest.current.onTap();
      }
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }, []);

  return { dragging, onPointerDown };
}

/** The 52px circular FAB's shared geometry / cursor / spring transition. */
export const FAB_SHELL_SIZE = 52;
export function agentFabStyle({ pos, dragging, open }) {
  const scale = open ? 0.92 : 1;
  return {
    position: 'fixed', left: 0, top: 0, width: FAB_SHELL_SIZE, height: FAB_SHELL_SIZE, borderRadius: '50%',
    border: 'none', cursor: dragging ? 'grabbing' : 'grab', zIndex: 9500, touchAction: 'none',
    transition: dragging ? 'none' : 'transform 240ms cubic-bezier(0.23, 1, 0.32, 1), background 200ms ease',
    transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`,
  };
}

/**
 * Where the non-modal panel sits: beside the FAB, flipped to whichever side has
 * room, with the transform-origin pointing back at the button.
 *  panelWidth     the panel's fixed width
 *  bottomReserve  vertical room the panel needs, so `top` never pushes it off-screen
 */
export function agentPanelAnchor({ pos, panelWidth, bottomReserve }) {
  const onRight = pos.x > window.innerWidth / 2;
  return {
    onRight,
    left: onRight ? Math.max(12, pos.x - panelWidth - 12) : Math.min(window.innerWidth - panelWidth - 12, pos.x + 64),
    top: Math.max(12, Math.min(pos.y - 150, window.innerHeight - bottomReserve)),
    originX: onRight ? '100%' : '0%',
  };
}

/**
 * The three shared keyframes — panel pop-in, chip enter, and the `--recording`
 * pulse ring. Namespaced per surface so each portal keeps its own animation
 * names (`cmd-*` / `ga-*`) exactly as before.
 */
export function agentFabKeyframes(prefix) {
  return `
        @keyframes ${prefix}-pop { from { opacity: 0; transform: scale(.8) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes ${prefix}-chip { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ${prefix}-ring { 0% { box-shadow: 0 0 0 0 hsl(var(--recording) / .45); } 70% { box-shadow: 0 0 0 13px hsl(var(--recording) / 0); } 100% { box-shadow: 0 0 0 0 hsl(var(--recording) / 0); } }`;
}
